import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { logError } from "@/shared/services/ErrorReporting";
import { db } from "@/integrations/firebase/firestore";
import { Job, JobStatus } from "./useJobs";

export const useJob = (jobId: string | null) => {
  const { authUser } = useAuth();
  const [job, setJob] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!authUser || !jobId) {
      setIsLoading(false);
      setJob(null);
      return;
    }

    const jobDocRef = doc(db, "jobs", jobId);

    const unsubscribe = onSnapshot(
      jobDocRef,
      (jobDoc) => {
        if (!jobDoc.exists()) {
          setJob(null);
          setIsLoading(false);
          return;
        }

        const data = jobDoc.data();

        const jobInstance = new Job(
          jobDoc.id,
          data.user_id,
          data.manuscript_uri,
          JobStatus[data.status as keyof typeof JobStatus],
          data.uploaded_at?.toDate() || new Date(),
          data.summary_uri || undefined,
          data.summary_id,
          data.desired_word_count || 2500,
          data.output_language || "en",
          data.marked_for_deletion ?? true,
          data.tenant_id,
          data.errors?.length ?? 0,
        );

        setJob(jobInstance);
        setIsLoading(false);
      },
      (err) => {
        logError(err);
        setError(err);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [authUser, jobId]);

  return { job, isLoading, error };
};
